import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, User, Mail, UserPlus, X, Building } from 'lucide-react';
import logger from '../utils/logger';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    company_name: '',
    terms_accepted: false,
    privacy_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) newErrors.username = 'Корисничкото име е задолжително';
    if (!formData.email.trim()) newErrors.email = 'E-mail е задолжителен';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'E-mail е невалиден';
    
    if (!formData.password) newErrors.password = 'Лозинката е задолжителна';
    else if (formData.password.length < 6) newErrors.password = 'Лозинката мора да има најмалку 6 карактери';
    
    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Лозинките не се совпаѓаат';
    }

    if (!formData.first_name.trim()) newErrors.first_name = 'Името е задолжително';
    if (!formData.last_name.trim()) newErrors.last_name = 'Презимето е задолжително';
    
    if (!formData.terms_accepted) {
      newErrors.terms_accepted = 'Мора да ги прифатите условите за користење';
    }
    
    if (!formData.privacy_accepted) {
      newErrors.privacy_accepted = 'Мора да ја прифатите политиката за приватност';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { confirm_password, ...registerData } = formData;
      const result = await register(registerData);
      if (result.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      logger.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-page)',
      padding: '2rem 1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
        width: '100%',
        maxWidth: '500px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '60px',
            height: '60px',
            backgroundColor: 'var(--color-primary)',
            borderRadius: '50%',
            marginBottom: '1rem'
          }}>
            <UserPlus size={30} color="white" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)', margin: '0' }}>Креирај нов профил
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Регистрирајте ја вашата флота денес
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Име</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                placeholder="Име"
                disabled={loading}
              />
              {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Презиме</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                placeholder="Презиме"
                disabled={loading}
              />
              {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Компанија (опционално)</label>
            <div style={{ position: 'relative' }}>
              <Building size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                style={{ paddingLeft: '35px' }}
                className="form-control"
                placeholder="Име на вашата фирма"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Корисничко име</label>
            <div style={{ position: 'relative' }}>
              <User size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                style={{ paddingLeft: '35px' }}
                className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                placeholder="Корисничко име"
                disabled={loading}
              />
            </div>
            {errors.username && <div className="invalid-feedback">{errors.username}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">E-mail адреса</label>
            <div style={{ position: 'relative' }}>
              <Mail size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{ paddingLeft: '35px' }}
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                placeholder="email@example.com"
                disabled={loading}
              />
            </div>
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Лозинка</label>
            <div style={{ position: 'relative' }}>
              <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={{ paddingLeft: '35px' }}
                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                placeholder="Лозинка"
                disabled={loading}
              />
            </div>
            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Потврди лозинка</label>
            <div style={{ position: 'relative' }}>
              <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                style={{ paddingLeft: '35px' }}
                className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                placeholder="Повторете ја лозинката"
                disabled={loading}
              />
            </div>
            {errors.confirm_password && <div className="invalid-feedback">{errors.confirm_password}</div>}
          </div>

          <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
            <div className="form-check" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '1rem' }}>
              <input
                className="form-check-input"
                type="checkbox"
                name="terms_accepted"
                id="terms_accepted"
                checked={formData.terms_accepted}
                onChange={handleChange}
                style={{ marginTop: '4px' }}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="terms_accepted" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                Се согласувам со <span 
                  onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                  style={{ color: 'var(--color-primary)', fontWeight: 'bold', textDecoration: 'underline' }}
                >Условите за користење</span>
              </label>
            </div>
            {errors.terms_accepted && <div style={{ color: 'var(--color-danger)', fontSize: '0.75rem', marginTop: '-0.75rem', marginBottom: '1rem' }}>{errors.terms_accepted}</div>}

            <div className="form-check" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <input
                className="form-check-input"
                type="checkbox"
                name="privacy_accepted"
                id="privacy_accepted"
                checked={formData.privacy_accepted}
                onChange={handleChange}
                style={{ marginTop: '4px' }}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="privacy_accepted" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                Ги прифаќам одредбите од <span 
                  onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                  style={{ color: 'var(--color-primary)', fontWeight: 'bold', textDecoration: 'underline' }}
                >Политиката за приватност</span>
              </label>
            </div>
            {errors.privacy_accepted && <div style={{ color: 'var(--color-danger)', fontSize: '0.75rem', marginTop: '0.25rem' }}>{errors.privacy_accepted}</div>}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', padding: '0.75rem', marginTop: '1rem' }}
            disabled={loading}
          >
            {loading ? 'Се регистрира...' : 'Регистрирај се'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Веќе имате профил? <Link to="/login" style={{ color: 'var(--color-primary)', fontWeight: 'bold', textDecoration: 'none' }}>Најавете се</Link>
          </p>
        </div>
      </div>

      {/* Terms Modal */}
      {showTermsModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>Услови за користење</h2>
              <button onClick={() => setShowTermsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={24} />
              </button>
            </div>
            <div style={{ padding: '1.5rem', overflowY: 'auto', fontSize: '0.95rem', lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              <p style={{ fontWeight: '700', marginBottom: '1rem' }}>УСЛОВИ ЗА КОРИСТЕЊЕ НА ПЛАТФОРМАТА НА ОВАТИК ДООЕЛ СКОПЈЕ</p>
              <p>Со регистрација и користење на овој софтвер, Вие (корисникот) склучувате договор со Оватик ДООЕЛ Скопје под следниве услови:</p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Опис на услугата:</p>
                <p>Платформата е мултитенант (multi-tenant) софтверско решение во облак за следење и управување со флота. Секој корисник добива технички изолиран систем (инстанца) со сопствена база на податоци.</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Изолација и доверливост:</p>
                <p>Системот е проектиран така што Оватик ДООЕЛ Скопје нема увид во локациите во реално време, ниту во историјата на движење на возилата на корисникот. Пристапот до овие информации го има исклучиво корисникот.</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Одговорност:</p>
                <p>Корисникот ја користи платформата на сопствена одговорност. Оватик ДООЕЛ Скопје не сноси одговорност за евентуални штети, загуба на профит или деловни прекини настанати поради користење или неможност за користење на софтверот.</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Техничка поддршка:</p>
                <p>Административен пристап до серверот од страна на Оватик ДООЕЛ Скопје е дозволен единствено за техничко одржување или решавање дефекти, без навлегување во содржината на податоците.</p>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Прекин на услугата:</p>
                <p>Корисникот може да го прекине користењето во секое време, по што неговите податоци се бришат согласно дефинираните безбедносни протоколи.</p>
              </div>
            </div>
            <div style={{ padding: '1.25rem', borderTop: '1px solid var(--border-color)', textAlign: 'right' }}>
              <button 
                onClick={() => setShowTermsModal(false)}
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacyModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>Политика за приватност</h2>
              <button onClick={() => setShowPrivacyModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={24} />
              </button>
            </div>
            <div style={{ padding: '1.5rem', overflowY: 'auto', fontSize: '0.95rem', lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              <p style={{ fontWeight: '700', marginBottom: '1rem' }}>ПОЛИТИКА ЗА ПРИВАТНОСТ НА ОВАТИК ДООЕЛ СКОПЈЕ</p>
              <p>Оваа Политика уредува како се обработуваат податоците генерирани преку ГПС уредите на корисникот.</p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Улоги во обработката:</p>
                <p>Оватик ДООЕЛ Скопје дејствува како Обработувач, додека корисникот (фирмата) дејствува како Контролор на личните податоци.</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Правна основа:</p>
                <p>Корисникот е исклучиво одговорен да обезбеди правна основа за следење (согласност од вработени или легитимен интерес) согласно Законот за заштита на личните податоци на Р.С. Македонија.</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Податоци кои се собираат:</p>
                <p>Платформата обработува ГПС координати, брзина, насока, статус на возило и кориснички имиња внесени од страна на клиентот.</p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Безбедност:</p>
                <p>Сите податоци се чуваат на заштитени сервери со примена на современи методи за енкрипција. Оватик ДООЕЛ Скопје не ги продава ниту ги споделува податоците со трети страни, освен во случај на законска обврска.</p>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <p style={{ fontWeight: '700', marginBottom: '0.25rem' }}>Вашите права:</p>
                <p>За сите прашања поврзани со вашите податоци или нивно бришење, можете да не контактирате преку вашиот административен панел.</p>
              </div>
            </div>
            <div style={{ padding: '1.25rem', borderTop: '1px solid var(--border-color)', textAlign: 'right' }}>
              <button 
                onClick={() => setShowPrivacyModal(false)}
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
